<template>
    <v-card>
        <v-dialog v-model="dialogDelete" max-width="600px" persistent>
            <v-card>
                <v-card-title>
                    <span class="headline red--text text--darken-1" v-text="$t('title.deleteTracker')"></span>
                </v-card-title>
                <v-card-text>
                    <span class="subtitle-2 text--darken-1" v-text="$t('title.deleteTrackerReplace')"></span>
                </v-card-text>

                <v-card-text>
                    <tracker-selector v-model="editTracker.replace" />
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey darken-1" text @click="cancelEdit">
                        {{ $t('buttons.cancel') }}
                    </v-btn>
                    <v-btn color="red" dark @click="deleteObject">
                        {{ $t('buttons.delete') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-card-text v-if="editTracker && dialog">
            <v-card flat>
                <v-card-title>
                    <span class="headline blue--text text--darken-1">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                    <v-text-field
                        v-model="editTracker.name"
                        :label="$t('trackers.attributes.name')"
                        required
                        color="red"
                    ></v-text-field>
                    <v-text-field
                        v-model="editTracker.label"
                        :label="$t('trackers.attributes.label')"
                        required
                        color="red"
                    ></v-text-field>
                    <v-textarea
                        v-model="editTracker.description"
                        :label="$t('trackers.attributes.description')"
                    ></v-textarea>
                    <v-color-picker
                        v-model="editTracker.color"
                        hide-canvas
                        hide-mode-switch
                        show-swatches
                        hide-inputs
                        flat
                        width="100%"
                        height="200"
                        class="mb-4"
                    ></v-color-picker>
                    <v-switch v-model="editTracker.default" :label="$t('trackers.attributes.default')"></v-switch>
                    <roles-selector
                        :label="$t('trackers.attributes.notifications')"
                        v-model="editTracker.notifications"
                    />
                    <roles-selector :label="$t('trackers.attributes.roles')" v-model="editTracker.roles" />

                    <status-selector
                        :label="$t('trackers.attributes.hiddenStatus')"
                        v-model="editTracker.hiddenStatus"
                    />

                    <issue-attr-selector
                        :label="$t('trackers.attributes.hiddenAttributes')"
                        v-model="editTracker.hiddenAttributes"
                    />
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey darken-1" text @click="cancelEdit">
                        {{ $t('buttons.cancel') }}
                    </v-btn>
                    <v-btn color="success" @click="saveObject">
                        {{ editTracker.id ? $t('buttons.refresh') : $t('buttons.add') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-card-text>

        <v-card-text v-else>
            <v-data-table
                :headers="headers"
                :items="trackers"
                sort-by="name"
                :items-per-page="20"
                :footer-props="{
                    'items-per-page-options': [10, 20, 50, 100, 200],
                    'show-current-page': true,
                    'show-first-last-page': true,
                }"
            >
                <template v-slot:top>
                    <v-toolbar flat color="transparent">
                        <v-spacer></v-spacer>
                        <v-btn rounded color="primary" dark @click="dialog = true">
                            {{ $t('buttons.createTracker') }}
                        </v-btn>
                    </v-toolbar>
                </template>

                <template v-slot:[`item.label`]="{ item }">
                    <span :class="item.default ? 'subtitle-2' : ''">{{ item.label }}</span>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-icon small class="mr-2" @click="editItem(item)"> mdi-pencil </v-icon>
                    <v-icon small @click="deleteItem(item)"> mdi-delete </v-icon>
                </template>
                <template v-slot:[`item.color`]="{ item }">
                    <v-avatar :color="item.color" size="36"> &nbsp; </v-avatar>
                </template>
                <template v-slot:[`item.notifications`]="{ item }">
                    <labelRoles :value="item.notifications" color="amber darken-2" />
                </template>
                <template v-slot:[`item.roles`]="{ item }">
                    <labelRoles :value="item.roles" color="teal darken-2" />
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import labelRoles from '@/components/labels/roles';
import rolesSelector from '@/components/selectors/multiple/roles';
import statusSelector from '@/components/selectors/multiple/status';
import issueAttrSelector from '@/components/selectors/multiple/issueAttributes';
import trackerSelector from '@/components/selectors/simple/tracker';
import Clone from 'lodash/cloneDeep';

export default {
    name: 'settings-trackers',
    data: () => ({
        dialog: false,
        dialogDelete: false,
        editTracker: {
            color: '#000000',
            hiddenAttributes: [],
        },
    }),
    created() {
        this.findTrackers();
    },
    components: {
        labelRoles,
        rolesSelector,
        statusSelector,
        issueAttrSelector,
        trackerSelector,
    },
    watch: {},
    computed: {
        ...mapGetters(['trackers']),
        headers() {
            return [
                {
                    text: '',
                    sortable: false,
                    value: 'color',
                    width: 36,
                },
                {
                    text: this.$t('trackers.attributes.name'),
                    sortable: true,
                    value: 'name',
                },
                {
                    text: this.$t('trackers.attributes.label'),
                    sortable: true,
                    value: 'label',
                },
                {
                    text: this.$t('trackers.attributes.notifications'),
                    sortable: false,
                    value: 'notifications',
                },
                {
                    text: this.$t('trackers.attributes.roles'),
                    sortable: false,
                    value: 'roles',
                },
                {
                    text: this.$t('trackers.attributes.actions'),
                    sortable: false,
                    value: 'actions',
                    width: 50,
                },
            ];
        },
        formTitle() {
            return this.editTracker.id ? this.$t('title.editTracker') : this.$t('title.newTracker');
        },
    },
    methods: {
        ...mapActions(['findTrackers', 'createTracker', 'updateTracker', 'deleteTracker']),
        cancelEdit() {
            this.dialog = false;
            this.dialogDelete = false;
            this.editTracker = { color: '#000000', hiddenAttributes: [] };
        },
        editItem(item) {
            this.dialog = true;
            this.editTracker = Clone(item);
        },
        deleteItem(item) {
            this.dialogDelete = true;
            this.editTracker = Clone(item);
        },
        deleteObject() {
            this.deleteTracker(this.editTracker);
            this.cancelEdit();
        },
        saveObject() {
            if (this.editTracker.id) {
                this.updateTracker(this.editTracker);
            } else {
                this.createTracker(this.editTracker);
            }
            this.cancelEdit();
        },
    },
};
</script>

<style></style>
